// Insurance & bonds — Darkspire Risk LLC's own coverage that satisfies the
// per-jurisdiction insurance / bond requirements. Three tabbed sections:
// policies (GL / E&O / WC / auto / umbrella), certificates (COIs filed with
// regulators or issued to clients), and surety bonds (with GA's bond /
// insurance / net-worth alternative election).
//
// NOTE: route + nav link for this page are wired in App.tsx / NavMenu during
// the Integration pass.

import { useState, type FormEvent } from 'react';
import type {
  InsurancePolicy,
  InsuranceLine,
  InsuranceForm,
  CertificateHolderKind,
  SuretyBond,
  BondAlternative,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import { DocumentAttachment } from '../components/DocumentAttachment';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import { ApiError } from '../api/client';
import {
  Button,
  Dialog,
  FormField,
  FormRow,
  Input,
  Table,
  Tabs,
  Textarea,
  type TabItem,
} from '../components/ui';
import {
  useInsurancePolicies,
  useCreateInsurancePolicy,
  useUpdateInsurancePolicy,
  useInsuranceCertificates,
  useCreateInsuranceCertificate,
  useBonds,
  useCreateBond,
  useUpdateBond,
} from '../api/queries/compliance';

// ---------- Static option lists ----------

const INSURANCE_LINES: { value: InsuranceLine; label: string }[] = [
  { value: 'gl', label: 'General liability' },
  { value: 'eo', label: 'Errors & omissions' },
  { value: 'wc', label: 'Workers comp' },
  { value: 'auto', label: 'Commercial auto' },
  { value: 'umbrella', label: 'Umbrella' },
];

const INSURANCE_FORMS: { value: InsuranceForm; label: string }[] = [
  { value: 'occurrence', label: 'Occurrence' },
  { value: 'claims_made', label: 'Claims made' },
];

const HOLDER_KINDS: { value: CertificateHolderKind; label: string }[] = [
  { value: 'regulator', label: 'Regulator' },
  { value: 'client', label: 'Client' },
  { value: 'other', label: 'Other' },
];

const BOND_ALTERNATIVES: { value: BondAlternative; label: string }[] = [
  { value: 'bond', label: 'Surety bond' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'net_worth', label: 'Net worth' },
];

// ---------- Helpers ----------

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 403) {
    return 'You do not have permission to do that.';
  }
  return err instanceof ApiError ? err.message : fallback;
}

function fmtDate(epochMs: number | null | undefined): string {
  return epochMs === null || epochMs === undefined
    ? '—'
    : new Date(epochMs).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      });
}

function fmtMoney(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function lineLabel(line: InsuranceLine): string {
  return INSURANCE_LINES.find((l) => l.value === line)?.label ?? line.toUpperCase();
}

function holderKindLabel(kind: CertificateHolderKind): string {
  return HOLDER_KINDS.find((h) => h.value === kind)?.label ?? kind;
}

function bondAltLabel(alt: BondAlternative): string {
  return BOND_ALTERNATIVES.find((a) => a.value === alt)?.label ?? alt;
}

// Parse an optional positive decimal amount string into a number for the wire.
function parseAmount(s: string): number | undefined {
  const t = s.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

// =============================================================================
// Page — tab shell
// =============================================================================

export function InsuranceBondsPage(): JSX.Element {
  const tabItems: TabItem[] = [
    { value: 'policies', label: 'Policies', content: <PoliciesSection /> },
    { value: 'certificates', label: 'Certificates', content: <CertificatesSection /> },
    { value: 'bonds', label: 'Bonds', content: <BondsSection /> },
  ];

  return (
    <section className="compliance-page">
      <header className="page-header">
        <div>
          <h2>Insurance &amp; bonds</h2>
          <p className="page-sub">
            Company coverage that satisfies per-jurisdiction insurance and bond requirements.
          </p>
        </div>
      </header>

      <Tabs items={tabItems} defaultValue="policies" />
    </section>
  );
}

// =============================================================================
// Policies section
// =============================================================================

function PoliciesSection(): JSX.Element {
  const canWrite = can('compliance.write');
  const policiesQuery = useInsurancePolicies();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<InsurancePolicy | null>(null);

  const policies = policiesQuery.data?.insurance_policies ?? [];

  return (
    <div className="tab-panel">
      <div className="card-toolbar">
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            New policy
          </Button>
        )}
      </div>

      {policiesQuery.isPending && <p className="muted">Loading policies…</p>}
      {policiesQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(policiesQuery.error, 'Failed to load policies.')}
        </p>
      )}
      {!policiesQuery.isPending && !policiesQuery.isError && policies.length === 0 && (
        <p className="muted empty-state">No insurance policies recorded yet.</p>
      )}

      {policies.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Carrier</Table.HeaderCell>
              <Table.HeaderCell>Policy #</Table.HeaderCell>
              <Table.HeaderCell>Line</Table.HeaderCell>
              <Table.HeaderCell>Per occurrence</Table.HeaderCell>
              <Table.HeaderCell>Aggregate</Table.HeaderCell>
              <Table.HeaderCell>Form</Table.HeaderCell>
              <Table.HeaderCell>Effective</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell>Document</Table.HeaderCell>
              {canWrite && <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {policies.map((p) => (
              <Table.Row key={p.id}>
                <Table.Cell tone="strong">{p.carrier}</Table.Cell>
                <Table.Cell>{p.policy_number ? <code>{p.policy_number}</code> : '—'}</Table.Cell>
                <Table.Cell>{lineLabel(p.line)}</Table.Cell>
                <Table.Cell>{fmtMoney(p.per_occurrence_limit)}</Table.Cell>
                <Table.Cell>{fmtMoney(p.aggregate_limit)}</Table.Cell>
                <Table.Cell>{p.form === 'occurrence' ? 'Occurrence' : 'Claims made'}</Table.Cell>
                <Table.Cell>{fmtDate(p.effective_date)}</Table.Cell>
                <Table.Cell>{fmtDate(p.expiration_date)}</Table.Cell>
                <Table.Cell><DocumentAttachment segment="insurance-policies" id={p.id} documentPath={p.document_path ?? null} /></Table.Cell>
                {canWrite && (
                  <Table.Cell className="col-actions">
                    <Button variant="ghost" className="btn-sm" onClick={() => setEditing(p)}>
                      Edit
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && <PolicyModal mode="create" onClose={() => setCreateOpen(false)} />}
      {editing && <PolicyModal mode="edit" policy={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

interface PolicyModalProps {
  mode: 'create' | 'edit';
  policy?: InsurancePolicy;
  onClose: () => void;
}

function PolicyModal({ mode, policy, onClose }: PolicyModalProps): JSX.Element {
  const create = useCreateInsurancePolicy();
  const update = useUpdateInsurancePolicy();
  const busy = create.isPending || update.isPending;

  const [carrier, setCarrier] = useState(policy?.carrier ?? '');
  const [policyNumber, setPolicyNumber] = useState(policy?.policy_number ?? '');
  const [line, setLine] = useState<InsuranceLine>(policy?.line ?? 'gl');
  const [perOccurrence, setPerOccurrence] = useState(policy?.per_occurrence_limit ?? '');
  const [aggregate, setAggregate] = useState(policy?.aggregate_limit ?? '');
  const [form, setForm] = useState<InsuranceForm>(policy?.form ?? 'occurrence');
  const [effectiveDate, setEffectiveDate] = useState<number | null>(policy?.effective_date ?? null);
  const [expirationDate, setExpirationDate] = useState<number | null>(policy?.expiration_date ?? null);
  const [cancellationDays, setCancellationDays] = useState(
    policy?.cancellation_notice_days !== null && policy?.cancellation_notice_days !== undefined
      ? String(policy.cancellation_notice_days)
      : '',
  );
  const [notes, setNotes] = useState(policy?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (carrier.trim() === '') {
      setError('Carrier is required.');
      return;
    }
    const cancel = cancellationDays.trim() === '' ? undefined : Number(cancellationDays);
    try {
      if (mode === 'create') {
        await create.mutateAsync({
          carrier: carrier.trim(),
          line,
          form,
          ...(policyNumber.trim() ? { policy_number: policyNumber.trim() } : {}),
          ...(parseAmount(perOccurrence) !== undefined ? { per_occurrence_limit: parseAmount(perOccurrence) } : {}),
          ...(parseAmount(aggregate) !== undefined ? { aggregate_limit: parseAmount(aggregate) } : {}),
          ...(effectiveDate !== null ? { effective_date: effectiveDate } : {}),
          ...(expirationDate !== null ? { expiration_date: expirationDate } : {}),
          ...(cancel !== undefined ? { cancellation_notice_days: cancel } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        });
        toast.success('Policy created.');
      } else if (policy) {
        await update.mutateAsync({
          id: policy.id,
          carrier: carrier.trim(),
          line,
          form,
          policy_number: policyNumber.trim() ? policyNumber.trim() : null,
          per_occurrence_limit: parseAmount(perOccurrence) ?? null,
          aggregate_limit: parseAmount(aggregate) ?? null,
          effective_date: effectiveDate,
          expiration_date: expirationDate,
          cancellation_notice_days: cancel ?? null,
          notes: notes.trim() ? notes.trim() : null,
        });
        toast.success('Policy updated.');
      }
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Could not save the policy.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'New policy' : 'Edit policy'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="policy-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="policy-form" className="form-grid" onSubmit={onSubmit}>
        <FormRow>
          <FormField label="Carrier" required>
            <Input
              type="text"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              maxLength={200}
              disabled={busy}
              required
              autoFocus
            />
          </FormField>
          <FormField label="Policy number">
            <Input
              type="text"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              maxLength={120}
              disabled={busy}
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Line">
            <select
              className="form-input"
              value={line}
              onChange={(e) => setLine(e.target.value as InsuranceLine)}
              disabled={busy}
            >
              {INSURANCE_LINES.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Form">
            <select
              className="form-input"
              value={form}
              onChange={(e) => setForm(e.target.value as InsuranceForm)}
              disabled={busy}
            >
              {INSURANCE_FORMS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Per-occurrence limit (USD)">
            <Input
              type="text"
              inputMode="decimal"
              value={perOccurrence}
              onChange={(e) => setPerOccurrence(e.target.value)}
              placeholder="e.g. 1000000"
              disabled={busy}
            />
          </FormField>
          <FormField label="Aggregate limit (USD)">
            <Input
              type="text"
              inputMode="decimal"
              value={aggregate}
              onChange={(e) => setAggregate(e.target.value)}
              placeholder="e.g. 2000000"
              disabled={busy}
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Effective date">
            <DateInput value={effectiveDate} onChange={setEffectiveDate} disabled={busy} />
          </FormField>
          <FormField label="Expiration date">
            <DateInput value={expirationDate} onChange={setExpirationDate} disabled={busy} />
          </FormField>
        </FormRow>
        <FormField label="Cancellation notice (days)">
          <Input
            type="number"
            value={cancellationDays}
            onChange={(e) => setCancellationDays(e.target.value)}
            min={0}
            disabled={busy}
          />
        </FormField>
        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={10_000}
            disabled={busy}
          />
        </FormField>
        {error && <p className="error-text" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// =============================================================================
// Certificates section
// =============================================================================

function CertificatesSection(): JSX.Element {
  const canWrite = can('compliance.write');
  const certsQuery = useInsuranceCertificates();
  const policiesQuery = useInsurancePolicies();

  const [createOpen, setCreateOpen] = useState(false);

  const certs = certsQuery.data?.insurance_certificates ?? [];
  const policies = policiesQuery.data?.insurance_policies ?? [];

  return (
    <div className="tab-panel">
      <div className="card-toolbar">
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            New certificate
          </Button>
        )}
      </div>

      {certsQuery.isPending && <p className="muted">Loading certificates…</p>}
      {certsQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(certsQuery.error, 'Failed to load certificates.')}
        </p>
      )}
      {!certsQuery.isPending && !certsQuery.isError && certs.length === 0 && (
        <p className="muted empty-state">No certificates recorded yet.</p>
      )}

      {certs.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Policy</Table.HeaderCell>
              <Table.HeaderCell>Holder kind</Table.HeaderCell>
              <Table.HeaderCell>Holder</Table.HeaderCell>
              <Table.HeaderCell>State form</Table.HeaderCell>
              <Table.HeaderCell>Filed</Table.HeaderCell>
              <Table.HeaderCell>Document</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {certs.map((c) => (
              <Table.Row key={c.id}>
                <Table.Cell tone="strong">
                  {c.policy_carrier ?? `Policy #${c.policy_id}`}
                  {c.policy_number ? ` (${c.policy_number})` : ''}
                </Table.Cell>
                <Table.Cell>{holderKindLabel(c.certificate_holder_kind)}</Table.Cell>
                <Table.Cell>{c.regulator_name ?? c.holder_text ?? (c.organization_id !== null ? `Org #${c.organization_id}` : '—')}</Table.Cell>
                <Table.Cell>{c.state_form_ref ?? '—'}</Table.Cell>
                <Table.Cell>{fmtDate(c.filed_at)}</Table.Cell>
                <Table.Cell><DocumentAttachment segment="insurance-certificates" id={c.id} documentPath={c.document_path ?? null} /></Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && (
        <CertificateModal policies={policies} onClose={() => setCreateOpen(false)} />
      )}
    </div>
  );
}

function CertificateModal({
  policies,
  onClose,
}: {
  policies: InsurancePolicy[];
  onClose: () => void;
}): JSX.Element {
  const create = useCreateInsuranceCertificate();
  const busy = create.isPending;

  const [policyId, setPolicyId] = useState('');
  const [holderKind, setHolderKind] = useState<CertificateHolderKind>('regulator');
  const [regulatorId, setRegulatorId] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [holderText, setHolderText] = useState('');
  const [stateFormRef, setStateFormRef] = useState('');
  const [filedAt, setFiledAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const pid = Number(policyId);
    if (!Number.isInteger(pid) || pid <= 0) {
      setError('Select a policy.');
      return;
    }
    const regId = regulatorId.trim() === '' ? undefined : Number(regulatorId);
    const orgId = organizationId.trim() === '' ? undefined : Number(organizationId);
    try {
      await create.mutateAsync({
        policy_id: pid,
        certificate_holder_kind: holderKind,
        ...(holderKind === 'regulator' && regId !== undefined ? { regulator_id: regId } : {}),
        ...(holderKind === 'client' && orgId !== undefined ? { organization_id: orgId } : {}),
        ...(holderText.trim() ? { holder_text: holderText.trim() } : {}),
        ...(stateFormRef.trim() ? { state_form_ref: stateFormRef.trim() } : {}),
        ...(filedAt !== null ? { filed_at: filedAt } : {}),
      });
      toast.success('Certificate created.');
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Could not save the certificate.'));
    }
  };

  return (
    <Dialog
      title="New certificate"
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="cert-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="cert-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Policy" required>
          <select
            className="form-input"
            value={policyId}
            onChange={(e) => setPolicyId(e.target.value)}
            disabled={busy}
            required
            autoFocus
          >
            <option value="">Select a policy…</option>
            {policies.map((p) => (
              <option key={p.id} value={p.id}>
                {p.carrier} — {lineLabel(p.line)}{p.policy_number ? ` (${p.policy_number})` : ''}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Holder kind">
          <select
            className="form-input"
            value={holderKind}
            onChange={(e) => setHolderKind(e.target.value as CertificateHolderKind)}
            disabled={busy}
          >
            {HOLDER_KINDS.map((h) => (
              <option key={h.value} value={h.value}>{h.label}</option>
            ))}
          </select>
        </FormField>
        {holderKind === 'regulator' && (
          <FormField label="Regulator id">
            <Input
              type="number"
              value={regulatorId}
              onChange={(e) => setRegulatorId(e.target.value)}
              min={1}
              disabled={busy}
            />
          </FormField>
        )}
        {holderKind === 'client' && (
          <FormField label="Organization id">
            <Input
              type="number"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              min={1}
              disabled={busy}
            />
          </FormField>
        )}
        <FormField label="Holder text">
          <Input
            type="text"
            value={holderText}
            onChange={(e) => setHolderText(e.target.value)}
            maxLength={300}
            disabled={busy}
            placeholder="Free-text holder (when not a linked regulator / org)"
          />
        </FormField>
        <FormRow>
          <FormField label="State form ref">
            <Input
              type="text"
              value={stateFormRef}
              onChange={(e) => setStateFormRef(e.target.value)}
              maxLength={120}
              disabled={busy}
              placeholder="e.g. TX PSP-05"
            />
          </FormField>
          <FormField label="Filed date">
            <DateInput value={filedAt} onChange={setFiledAt} disabled={busy} />
          </FormField>
        </FormRow>
        {error && <p className="error-text" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// =============================================================================
// Bonds section
// =============================================================================

function BondsSection(): JSX.Element {
  const canWrite = can('compliance.write');
  const bondsQuery = useBonds();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<SuretyBond | null>(null);

  const bonds = bondsQuery.data?.bonds ?? [];

  return (
    <div className="tab-panel">
      <div className="card-toolbar">
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            New bond
          </Button>
        )}
      </div>

      {bondsQuery.isPending && <p className="muted">Loading bonds…</p>}
      {bondsQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(bondsQuery.error, 'Failed to load bonds.')}
        </p>
      )}
      {!bondsQuery.isPending && !bondsQuery.isError && bonds.length === 0 && (
        <p className="muted empty-state">No surety bonds recorded yet.</p>
      )}

      {bonds.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Carrier</Table.HeaderCell>
              <Table.HeaderCell>Bond #</Table.HeaderCell>
              <Table.HeaderCell>Amount</Table.HeaderCell>
              <Table.HeaderCell>Obligee</Table.HeaderCell>
              <Table.HeaderCell>Jurisdiction</Table.HeaderCell>
              <Table.HeaderCell>Alternative</Table.HeaderCell>
              <Table.HeaderCell>Effective</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell>Document</Table.HeaderCell>
              {canWrite && <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {bonds.map((b) => (
              <Table.Row key={b.id}>
                <Table.Cell tone="strong">{b.surety_carrier}</Table.Cell>
                <Table.Cell>{b.bond_number ? <code>{b.bond_number}</code> : '—'}</Table.Cell>
                <Table.Cell>{fmtMoney(b.amount)}</Table.Cell>
                <Table.Cell>{b.obligee ?? '—'}</Table.Cell>
                <Table.Cell>{b.jurisdiction_name ?? b.jurisdiction_code ?? `#${b.jurisdiction_id}`}</Table.Cell>
                <Table.Cell>{bondAltLabel(b.alternative_elected)}</Table.Cell>
                <Table.Cell>{fmtDate(b.effective_date)}</Table.Cell>
                <Table.Cell>{fmtDate(b.expiration_date)}</Table.Cell>
                <Table.Cell><DocumentAttachment segment="bonds" id={b.id} documentPath={b.document_path ?? null} /></Table.Cell>
                {canWrite && (
                  <Table.Cell className="col-actions">
                    <Button variant="ghost" className="btn-sm" onClick={() => setEditing(b)}>
                      Edit
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && <BondModal mode="create" onClose={() => setCreateOpen(false)} />}
      {editing && <BondModal mode="edit" bond={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

interface BondModalProps {
  mode: 'create' | 'edit';
  bond?: SuretyBond;
  onClose: () => void;
}

function BondModal({ mode, bond, onClose }: BondModalProps): JSX.Element {
  const create = useCreateBond();
  const update = useUpdateBond();
  const busy = create.isPending || update.isPending;

  const [carrier, setCarrier] = useState(bond?.surety_carrier ?? '');
  const [bondNumber, setBondNumber] = useState(bond?.bond_number ?? '');
  const [amount, setAmount] = useState(bond?.amount ?? '');
  const [obligee, setObligee] = useState(bond?.obligee ?? '');
  const [jurisdictionId, setJurisdictionId] = useState(
    bond ? String(bond.jurisdiction_id) : '',
  );
  const [effectiveDate, setEffectiveDate] = useState<number | null>(bond?.effective_date ?? null);
  const [expirationDate, setExpirationDate] = useState<number | null>(bond?.expiration_date ?? null);
  const [alternative, setAlternative] = useState<BondAlternative>(bond?.alternative_elected ?? 'bond');
  const [notes, setNotes] = useState(bond?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (carrier.trim() === '') {
      setError('Surety carrier is required.');
      return;
    }
    const jid = Number(jurisdictionId);
    if (!Number.isInteger(jid) || jid <= 0) {
      setError('A jurisdiction id is required.');
      return;
    }
    try {
      if (mode === 'create') {
        await create.mutateAsync({
          surety_carrier: carrier.trim(),
          jurisdiction_id: jid,
          alternative_elected: alternative,
          ...(bondNumber.trim() ? { bond_number: bondNumber.trim() } : {}),
          ...(parseAmount(amount) !== undefined ? { amount: parseAmount(amount) } : {}),
          ...(obligee.trim() ? { obligee: obligee.trim() } : {}),
          ...(effectiveDate !== null ? { effective_date: effectiveDate } : {}),
          ...(expirationDate !== null ? { expiration_date: expirationDate } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        });
        toast.success('Bond created.');
      } else if (bond) {
        await update.mutateAsync({
          id: bond.id,
          surety_carrier: carrier.trim(),
          jurisdiction_id: jid,
          alternative_elected: alternative,
          bond_number: bondNumber.trim() ? bondNumber.trim() : null,
          amount: parseAmount(amount) ?? null,
          obligee: obligee.trim() ? obligee.trim() : null,
          effective_date: effectiveDate,
          expiration_date: expirationDate,
          notes: notes.trim() ? notes.trim() : null,
        });
        toast.success('Bond updated.');
      }
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Could not save the bond.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'New bond' : 'Edit bond'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="bond-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="bond-form" className="form-grid" onSubmit={onSubmit}>
        <FormRow>
          <FormField label="Surety carrier" required>
            <Input
              type="text"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              maxLength={200}
              disabled={busy}
              required
              autoFocus
            />
          </FormField>
          <FormField label="Bond number">
            <Input
              type="text"
              value={bondNumber}
              onChange={(e) => setBondNumber(e.target.value)}
              maxLength={120}
              disabled={busy}
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Amount (USD)">
            <Input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 25000"
              disabled={busy}
            />
          </FormField>
          <FormField label="Obligee">
            <Input
              type="text"
              value={obligee}
              onChange={(e) => setObligee(e.target.value)}
              maxLength={300}
              disabled={busy}
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Jurisdiction id" required>
            <Input
              type="number"
              value={jurisdictionId}
              onChange={(e) => setJurisdictionId(e.target.value)}
              min={1}
              disabled={busy}
              required
            />
          </FormField>
          <FormField label="Alternative elected">
            <select
              className="form-input"
              value={alternative}
              onChange={(e) => setAlternative(e.target.value as BondAlternative)}
              disabled={busy}
            >
              {BOND_ALTERNATIVES.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Effective date">
            <DateInput value={effectiveDate} onChange={setEffectiveDate} disabled={busy} />
          </FormField>
          <FormField label="Expiration date">
            <DateInput value={expirationDate} onChange={setExpirationDate} disabled={busy} />
          </FormField>
        </FormRow>
        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={10_000}
            disabled={busy}
          />
        </FormField>
        {error && <p className="error-text" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}
